import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Calendar, Check, Restart, Flash, Xmark, EditPencil } from "iconoir-react";
import { fetchChores, createChore, updateChore, firstName } from "@/lib/api";
import { avatarColor, initials } from "@/pages/MiloApp";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

export default function ChoresTab({ session, members, onRefresh }) {
    const { t } = useT();
    const FILTERS = [
        { id: "mine", label: t("chores.filter.mine") },
        { id: "all", label: t("chores.filter.all") },
        { id: "done", label: t("chores.filter.done") },
    ];
    const [chores, setChores] = useState([]);
    const [title, setTitle] = useState("");
    const [assignee, setAssignee] = useState("");
    const [due, setDue] = useState("");
    const [recurrence, setRecurrence] = useState("");
    const [expandAdd, setExpandAdd] = useState(false);
    const [filter, setFilter] = useState("all");
    const [editingId, setEditingId] = useState(null);

    const load = async () => {
        const res = await fetchChores(session.household_id);
        setChores(res);
    };
    useEffect(() => {
        load();
    }, [session.household_id]);

    const resetForm = () => {
        setTitle("");
        setAssignee("");
        setDue("");
        setRecurrence("");
        setExpandAdd(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim()) {
            toast.error(t("chores.needs_title"));
            return;
        }
        await createChore(session.household_id, {
            title: title.trim(),
            assigned_member_id: assignee || null,
            due_date: due || null,
            recurrence: recurrence || null,
        });
        resetForm();
        await load();
        onRefresh?.();
        toast.success(recurrence ? t("chores.added_recurring", { rec: recurrence }) : t("chores.added"));
    };

    const toggleDone = async (c) => {
        const nextStatus = c.status === "done" ? "pending" : "done";
        try {
            await updateChore(session.household_id, c.id, { status: nextStatus });
        } catch (e) {
            toast.error(t("chores.couldnt_update"));
            return;
        }
        await load();
        onRefresh?.();
    };

    const snooze = async (c) => {
        const t2 = new Date();
        t2.setDate(t2.getDate() + 1);
        const iso = t2.toISOString().slice(0, 10);
        await updateChore(session.household_id, c.id, { due_date: iso });
        await load();
        onRefresh?.();
        toast.success(t("chores.pushed_tomorrow"));
    };

    const saveEdit = async (id, updates) => {
        await updateChore(session.household_id, id, updates);
        setEditingId(null);
        await load();
        onRefresh?.();
        toast.success(t("chores.updated"));
    };

    const memberOf = (mid) => members.find((m) => m.id === mid);

    const byAssignee = (c) =>
        filter === "mine"
            ? c.assigned_member_id === session.member_id
            : true;

    // Classic todo behavior: completed chores disappear from active filters
    // and only show in the "Done" filter.
    const filtered = chores
        .filter((c) => {
            if (filter === "done") return c.status === "done";
            return c.status !== "done";
        })
        .filter(byAssignee);

    const today = new Date().toISOString().slice(0, 10);
    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    const dueToday = filtered.filter((c) => c.due_date && c.due_date <= today);
    const dueTomorrow = filtered.filter((c) => c.due_date === tomorrow);
    const dueLater = filtered.filter((c) => !c.due_date || c.due_date > tomorrow);

    return (
        <div data-testid="chores-tab" className="space-y-5">
            <div>
                <div className="text-xs font-semibold text-[#5b6d63] uppercase tracking-widest">{t("chores.eyebrow")}</div>
                <div className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight mt-1">
                    {t("chores.title")}
                </div>
            </div>

            {/* Compact add — single field + Add. Expands on focus to show assignee + due + Save. */}
            <form onSubmit={submit} className="space-y-2">
                <div className="flex gap-2">
                    <input
                        data-testid="chore-title-input"
                        className="brut-input"
                        placeholder={t("chores.add_placeholder")}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onFocus={() => setExpandAdd(true)}
                    />
                    {!expandAdd && (
                        <button data-testid="submit-chore-btn" type="submit" className="brut-btn">
                            <Plus className="w-4 h-4" /> {t("chores.add_btn")}
                        </button>
                    )}
                </div>
                <AnimatePresence>
                    {expandAdd && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="grid grid-cols-2 gap-2 pt-1">
                                <select
                                    data-testid="chore-assignee-select"
                                    className="brut-input !py-2 !text-sm"
                                    value={assignee}
                                    onChange={(e) => setAssignee(e.target.value)}
                                >
                                    <option value="">{t("chores.assign_to")}</option>
                                    {members.map((m) => (
                                        <option key={m.id} value={m.id}>{firstName(m.name)}</option>
                                    ))}
                                </select>
                                <input
                                    data-testid="chore-due-input"
                                    type="date"
                                    className="brut-input !py-2 !text-sm"
                                    value={due}
                                    onChange={(e) => setDue(e.target.value)}
                                />
                            </div>
                            <div className="pt-2">
                                <select
                                    data-testid="chore-recurrence-select"
                                    className="brut-input !py-2 !text-sm w-full"
                                    value={recurrence}
                                    onChange={(e) => setRecurrence(e.target.value)}
                                >
                                    <option value="">{t("chores.recurrence.none")}</option>
                                    <option value="daily">{t("chores.recurrence.daily")}</option>
                                    <option value="weekly">{t("chores.recurrence.weekly")}</option>
                                    <option value="biweekly">{t("chores.recurrence.biweekly")}</option>
                                    <option value="monthly">{t("chores.recurrence.monthly")}</option>
                                </select>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button
                                    data-testid="submit-chore-btn"
                                    type="submit"
                                    className="brut-btn flex-1 justify-center text-sm"
                                >
                                    <Check className="w-3.5 h-3.5" /> {t("chores.save")}
                                </button>
                                <button
                                    type="button"
                                    data-testid="cancel-chore-btn"
                                    onClick={resetForm}
                                    className="brut-btn brut-btn-secondary text-sm"
                                >
                                    <Xmark className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </form>

            {/* Filter pills */}
            <div className="flex gap-2 overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6 scrollbar-hide">
                {FILTERS.map((f) => {
                    const active = filter === f.id;
                    return (
                        <button
                            key={f.id}
                            data-testid={`filter-${f.id}-btn`}
                            onClick={() => setFilter(f.id)}
                            className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${active ? "bg-[#2F6B43] text-white" : "bg-white border border-[#0d2a1b]/8 hover:bg-[#faf5ea]"}`}
                        >
                            {f.label}
                        </button>
                    );
                })}
            </div>

            {filter !== "done" ? (
                <>
                    {dueToday.length > 0 && (
                        <ChoreGroup
                            title={t("chores.section.due_today")}
                            count={dueToday.length}
                            urgent
                            chores={dueToday}
                            members={members}
                            memberOf={memberOf}
                            onDone={toggleDone}
                            onSnooze={snooze}
                            editingId={editingId}
                            setEditingId={setEditingId}
                            onSaveEdit={saveEdit}
                            t={t}
                        />
                    )}
                    {dueTomorrow.length > 0 && (
                        <ChoreGroup
                            title={t("chores.section.tomorrow")}
                            count={dueTomorrow.length}
                            chores={dueTomorrow}
                            members={members}
                            memberOf={memberOf}
                            onDone={toggleDone}
                            onSnooze={snooze}
                            editingId={editingId}
                            setEditingId={setEditingId}
                            onSaveEdit={saveEdit}
                            t={t}
                        />
                    )}
                    {dueLater.length > 0 && (
                        <ChoreGroup
                            title={t("chores.section.upcoming")}
                            count={dueLater.length}
                            chores={dueLater}
                            members={members}
                            memberOf={memberOf}
                            onDone={toggleDone}
                            onSnooze={snooze}
                            editingId={editingId}
                            setEditingId={setEditingId}
                            onSaveEdit={saveEdit}
                            t={t}
                        />
                    )}
                    {filtered.length === 0 && (
                        <div className="brut-card p-8 text-center">
                            <div className="font-display text-xl font-semibold mb-1">{t("chores.clean_slate")}</div>
                            <div className="text-sm text-[#5b6d63]">{t("chores.empty_subtitle")}</div>
                        </div>
                    )}
                </>
            ) : (
                <section className="brut-card p-5">
                    <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#5b6d63] mb-3">
                        {t("chores.completed_count", { n: filtered.length })}
                    </div>
                    {filtered.length === 0 ? (
                        <div className="text-sm text-[#5b6d63]">{t("chores.no_completed")}</div>
                    ) : (
                        <ul>
                            {filtered.map((c) => (
                                <li key={c.id} className="flex items-center gap-3 py-2.5 border-b border-[#0d2a1b]/5 last:border-0">
                                    <button
                                        data-testid={`undo-chore-${c.id}-btn`}
                                        onClick={() => toggleDone(c)}
                                        className="w-5 h-5 rounded-full bg-[#2F6B43] text-white flex items-center justify-center shrink-0"
                                    >
                                        <Check className="w-3 h-3" strokeWidth={3} />
                                    </button>
                                    <div className="flex-1 text-sm line-through text-[#5b6d63]">{c.title}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            )}
        </div>
    );
}

function ChoreGroup({ title, count, urgent, chores, members, memberOf, onDone, onSnooze, editingId, setEditingId, onSaveEdit, t }) {
    return (
        <section className="brut-card p-5">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#5b6d63]">
                    {title}
                </div>
                <span className={`chip ${urgent ? "chip-rose" : ""}`}>{count}</span>
            </div>
            <ul>
                {chores.map((c) => {
                    const m = memberOf(c.assigned_member_id);
                    const isEditing = editingId === c.id;
                    return (
                        <li key={c.id} className="py-2.5 border-b border-[#0d2a1b]/5 last:border-0">
                            {isEditing ? (
                                <EditChoreForm
                                    chore={c}
                                    members={members}
                                    onCancel={() => setEditingId(null)}
                                    onSave={(updates) => onSaveEdit(c.id, updates)}
                                />
                            ) : (
                                <div className="flex items-center gap-3">
                                    <button
                                        data-testid={`done-chore-${c.id}-btn`}
                                        onClick={() => onDone(c)}
                                        className="w-5 h-5 rounded-full border-[1.5px] border-[#2F6B43]/40 hover:bg-[#e8f1e4] hover:border-[#2F6B43] transition-colors shrink-0"
                                        aria-label="done"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm truncate">{c.title}</div>
                                        <div className="flex items-center gap-2 text-[11px] text-[#5b6d63] mt-0.5">
                                            {m && (
                                                <span className="flex items-center gap-1">
                                                    <div
                                                        className="avatar !w-4 !h-4 !text-[8px] !border-white"
                                                        style={{ background: avatarColor(m.name || m.id) }}
                                                    >
                                                        {initials(m.name)}
                                                    </div>
                                                    {firstName(m.name)}
                                                </span>
                                            )}
                                            {c.due_date && (
                                                <span className="flex items-center gap-0.5">
                                                    <Calendar className="w-3 h-3" /> {c.due_date}
                                                </span>
                                            )}
                                            {c.recurrence && (
                                                <span
                                                    data-testid={`recurrence-badge-${c.id}`}
                                                    className="flex items-center gap-0.5 text-[#2F6B43] font-semibold"
                                                    title={t("chores.repeats_tooltip", { rec: c.recurrence })}
                                                >
                                                    <Restart className="w-3 h-3" /> {t(`chores.recurrence.${c.recurrence}`)}
                                                </span>
                                            )}
                                            {urgent && (
                                                <span className="chip chip-rose !py-0.5 !px-2 !text-[10px] !font-bold">
                                                    <Flash className="w-2.5 h-2.5" /> {t("chores.urgent")}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        data-testid={`edit-chore-${c.id}-btn`}
                                        onClick={() => setEditingId(c.id)}
                                        className="w-7 h-7 rounded-full bg-[#faf5ea] hover:bg-[#e8f1e4] flex items-center justify-center shrink-0"
                                        aria-label="edit"
                                        title={t("chores.edit_tooltip")}
                                    >
                                        <EditPencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        data-testid={`snooze-chore-${c.id}-btn`}
                                        onClick={() => onSnooze(c)}
                                        className="w-7 h-7 rounded-full bg-[#faf5ea] hover:bg-[#fbeecb] flex items-center justify-center shrink-0"
                                        aria-label="snooze"
                                        title={t("chores.snooze_tooltip")}
                                    >
                                        <Restart className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

function EditChoreForm({ chore, members, onCancel, onSave }) {
    const { t } = useT();
    const [title, setTitle] = useState(chore.title || "");
    const [assignee, setAssignee] = useState(chore.assigned_member_id || "");
    const [due, setDue] = useState(chore.due_date || "");
    const [recurrence, setRecurrence] = useState(chore.recurrence || "");

    const submit = (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        onSave({
            title: title.trim(),
            assigned_member_id: assignee || null,
            due_date: due || null,
            recurrence: recurrence || null,
        });
    };

    return (
        <form onSubmit={submit} className="space-y-2" data-testid={`edit-form-${chore.id}`}>
            <input
                data-testid={`edit-title-input-${chore.id}`}
                className="brut-input !py-2 !text-sm"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
            />
            <div className="grid grid-cols-2 gap-2">
                <select
                    data-testid={`edit-assignee-${chore.id}`}
                    className="brut-input !py-2 !text-sm"
                    value={assignee}
                    onChange={(e) => setAssignee(e.target.value)}
                >
                    <option value="">{t("chores.unassigned")}</option>
                    {members.map((m) => (
                        <option key={m.id} value={m.id}>{firstName(m.name)}</option>
                    ))}
                </select>
                <input
                    data-testid={`edit-due-${chore.id}`}
                    type="date"
                    className="brut-input !py-2 !text-sm"
                    value={due}
                    onChange={(e) => setDue(e.target.value)}
                />
            </div>
            <select
                data-testid={`edit-recurrence-${chore.id}`}
                className="brut-input !py-2 !text-sm w-full"
                value={recurrence}
                onChange={(e) => setRecurrence(e.target.value)}
            >
                <option value="">{t("chores.recurrence.none")}</option>
                <option value="daily">{t("chores.recurrence.daily")}</option>
                <option value="weekly">{t("chores.recurrence.weekly")}</option>
                <option value="biweekly">{t("chores.recurrence.biweekly")}</option>
                <option value="monthly">{t("chores.recurrence.monthly")}</option>
            </select>
            <div className="flex gap-2">
                <button
                    data-testid={`save-edit-${chore.id}-btn`}
                    type="submit"
                    className="brut-btn text-xs flex-1 justify-center"
                >
                    <Check className="w-3.5 h-3.5" /> {t("common.save")}
                </button>
                <button
                    data-testid={`cancel-edit-${chore.id}-btn`}
                    type="button"
                    onClick={onCancel}
                    className="brut-btn brut-btn-secondary text-xs"
                >
                    <Xmark className="w-3.5 h-3.5" />
                </button>
            </div>
        </form>
    );
}
